import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { MaintenanceSchedulingDetailCaptureTableModel } from 'src/app/models/tables/maintenance/maintenance-scheduling-detail-capture-table.model';
import { MaintenanceSchedulingDetailTableModel } from 'src/app/models/tables/maintenance/maintenance-scheduling-detail-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MaintenanceSchedulingDetailService {
  private readonly _httpClient = inject(HttpClient);

  deleteSomeDetails(maintenanceSchedulingDetailIds: Array<number>): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.DeleteSomeDetailsAsync}`,
      maintenanceSchedulingDetailIds
    );
  }

  exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null = null, ascendant: boolean = true): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.ExportDetailConsultation}`,
      filters, { params, responseType: 'blob' }
    );
  }

  exportProcessErrorsTxt(alerts: Array<PreInvoicingAlertModel>): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.ExportProcessErrorsTxt}`, alerts,
      {
        responseType: 'blob'
      }
    );
  }

  generateMaintenanceOrders(maintenanceSchedulingId: number, listMaintenancesSchedulingId: number[]): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GenerateMaintenanceOrdersAsync}/${maintenanceSchedulingId}`,
      listMaintenancesSchedulingId
    );
  }

  getAllMaintenanceSchedulingDetailsWithPagination(page: number = 1, limit: number = 10, columnSort: string | null = null, ascendant: boolean = false, filters: ColumnResponseModel[]): Observable<PagedResponseModel<MaintenanceSchedulingDetailTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
    return this._httpClient.post<PagedResponseModel<MaintenanceSchedulingDetailTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetAllMaintenanceSchedulingDetailsWithPaginationAsync}`,
      filters,
      { params }
    );
  }

  getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetColumnsForDetailConsultationAsync}`
    );
  }

  getColumnsDetailsForCapture(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetColumnsDetailsForCaptureAsync}`
    );
  }

  getColumnsDetailByDaysForCapture(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetColumnsDetailByDaysForCaptureAsync}`
    );
  }

  getColumnsForArticles(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetColumnsForArticlesAsync}`
    );
  }

  getColumnsForPendingMaintenanceSchedulingByOrder(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetColumnsForPendingMaintenanceSchedulingByOrderAsync}`
    );
  }

  getPendingMaintenanceSchedulingByOrder(maintenanceSchedulingId: number, page: number = 1, limit: number = 10, ascendant: boolean = true, columnSort: string | null = null, filters: ColumnResponseModel[]): Observable<PagedResponseModel<MaintenanceSchedulingDetailTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant)
      .set('maintenanceSchedulingId', maintenanceSchedulingId);

    return this._httpClient.post<PagedResponseModel<MaintenanceSchedulingDetailTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetPendingMaintenanceSchedulingByOrderAsync}`,
      filters, { params }
    );
  }

  getDetailsForCapture(maintenanceSchedulingId: number, page: number = 1, limit: number = 10, ascendant: boolean = true, columnSort: string | null = null): Observable<PagedResponseModel<MaintenanceSchedulingDetailCaptureTableModel>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort ?? '')
      .set('ascendant', ascendant)
      .set('maintenanceSchedulingId', maintenanceSchedulingId);

    return this._httpClient.get<PagedResponseModel<MaintenanceSchedulingDetailCaptureTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetDetailsForCaptureAsync}`,
      { params }
    );
  }

  getAllArticles(maintenanceSchedulingId: number, page: number = 1, limit: number = 10, columnSort: string | null = null, ascendant: boolean = false, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('maintenanceSchedulingId', maintenanceSchedulingId);
    return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.GetAllArticlesAsync}`,
      filters, { params }
    );
  }

  processMaintenanceSchedulingByDays(maintenanceSchedulingId: number, listArticle: number[]): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.ProcessMaintenanceSchedulingByDaysAsync}/${maintenanceSchedulingId}`,
      listArticle
    );
  }

  processMaintenanceSchedulingByHourometer(maintenanceSchedulingId: number, listArticle: number[]): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(
      `${environment.apiCervinoBackend}${enpointCervinoWeb.maintenanceSchedulingDetail.ProcessMaintenanceSchedulingByHourometerAsync}/${maintenanceSchedulingId}`,
      listArticle
    );
  }

}
